import React from 'react';
import {ImagePosition} from '../constants';
import styles from '../Settings.module.css';

interface SettingsImageEditorModalProps {
  customHeaderImage: string;
  imagePosition: ImagePosition;
  imageZoom: number;
  error: string | null;
  isSaving: boolean;
  onCancel: () => void;
  onSave: () => void;
  onChangeImagePosition: (position: ImagePosition) => void;
  onChangeImageZoom: (zoom: number) => void;
}

const SettingsImageEditorModal: React.FC<SettingsImageEditorModalProps> = ({
  customHeaderImage,
  imagePosition,
  imageZoom,
  error,
  isSaving,
  onCancel,
  onSave,
  onChangeImagePosition,
  onChangeImageZoom,
}) => (
  <div
    className={`${styles.modalBackdrop} ${styles.editorBackdrop}`}
    onClick={() => {
      if (!isSaving) {
        onCancel();
      }
    }}
  >
    <div
      className={`${styles.modalCard} ${styles.editorModalCard} ${isSaving ? styles.editorModalCardSaving : ''}`.trim()}
      onClick={(event) => event.stopPropagation()}
    >
      <h2 className={styles.modalHeading}>✏️ Edit Header Image</h2>

      <div
        className={styles.editorPreview}
        style={{
          backgroundImage: `url(${customHeaderImage})`,
          backgroundSize: `${imageZoom * 100}%`,
          backgroundPosition: `${imagePosition.x}% ${imagePosition.y}%`,
        }}
      >
        <div className={styles.editorPreviewBadge}>Preview</div>
      </div>

      <div className={styles.editorControls}>
        <div className={styles.editorControlGroup}>
          <label className={styles.editorControlLabelRow}>
            <span style={{fontSize: '20px'}}>🔍</span>
            <span className={styles.editorControlLabel}>Zoom</span>
            <span className={styles.editorControlMeta}>({Math.round(imageZoom * 100)}%)</span>
          </label>
          <input
            type="range"
            min="0.5"
            max="3"
            step="0.1"
            value={imageZoom}
            disabled={isSaving}
            onChange={(event) => onChangeImageZoom(parseFloat(event.target.value))}
            className={styles.editorRange}
          />
          <div className={styles.editorRangeHint}>
            <span>Zoom Out (50%)</span>
            <span>Zoom In (300%)</span>
          </div>
        </div>

        <div className={styles.editorControlGroup}>
          <label className={styles.editorControlLabelRow}>
            <span style={{fontSize: '20px'}}>↔️</span>
            <span className={styles.editorControlLabel}>Horizontal Position</span>
          </label>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={imagePosition.x}
            disabled={isSaving}
            onChange={(event) => onChangeImagePosition({...imagePosition, x: parseInt(event.target.value, 10)})}
            className={styles.editorRange}
          />
          <div className={styles.editorRangeHint}>
            <span>← Left</span>
            <span>Center</span>
            <span>Right →</span>
          </div>
        </div>

        <div className={styles.editorControlGroup}>
          <label className={styles.editorControlLabelRow}>
            <span style={{fontSize: '20px'}}>↕️</span>
            <span className={styles.editorControlLabel}>Vertical Position</span>
          </label>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={imagePosition.y}
            disabled={isSaving}
            onChange={(event) => onChangeImagePosition({...imagePosition, y: parseInt(event.target.value, 10)})}
            className={styles.editorRange}
          />
          <div className={styles.editorRangeHint}>
            <span>↑ Top</span>
            <span>Center</span>
            <span>Bottom ↓</span>
          </div>
        </div>

        <div>
          <label className={styles.appearanceBlockLabel}>⚡ Quick Presets</label>
          <div className={styles.presetButtons}>
            <button
              type="button"
              disabled={isSaving}
              onClick={() => {
                onChangeImagePosition({x: 50, y: 50});
                onChangeImageZoom(1);
              }}
              className={styles.secondaryButton}
            >
              Center
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={() => onChangeImagePosition({x: 50, y: 30})}
              className={styles.secondaryButton}
            >
              Top
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={() => onChangeImagePosition({x: 50, y: 70})}
              className={styles.secondaryButton}
            >
              Bottom
            </button>
            <button
              type="button"
              disabled={isSaving}
              onClick={() => onChangeImageZoom(1)}
              className={styles.secondaryButton}
            >
              Reset Zoom
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <p className={styles.appearanceErrorText}>{error}</p>
      ) : null}

      <div className={styles.modalActions}>
        <button type="button" disabled={isSaving} onClick={onCancel} className={styles.cancelButton}>
          Cancel
        </button>
        <button type="button" disabled={isSaving} onClick={onSave} className={styles.primaryButton}>
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  </div>
);

export default React.memo(SettingsImageEditorModal);